import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import SEOHead from '../../../components/base/SEOHead';
import Navbar from '../../../components/feature/Navbar';

interface CryptoOption {
  id: string;
  name: string;
  symbol: string;
  icon: string;
  address: string;
  network: string;
  addressType: string;
}

interface Merchant {
  id: string;
  name: string;
  email: string;
  wallet_balance: number;
  total_earnings: number;
  rating: number;
}

// Permanent crypto addresses for receiving payments
const PERMANENT_CRYPTO_ADDRESSES: CryptoOption[] = [
  {
    id: 'bitcoin',
    name: 'Bitcoin',
    symbol: 'BTC',
    icon: 'ri-bit-coin-line',
    address: '3JjrAhRdyVHMhfeGxV492kQTTqTfohcsk7',
    network: 'Bitcoin Mainnet',
    addressType: 'P2SH'
  },
  {
    id: 'ethereum',
    name: 'Ethereum',
    symbol: 'ETH',
    icon: 'ri-ethereum-line',
    address: '0x8D156d907934F138b2bB204710EB98A57f17635f',
    network: 'Ethereum Mainnet',
    addressType: 'ERC-20'
  },
  {
    id: 'usdt',
    name: 'Tether USD',
    symbol: 'USDT',
    icon: 'ri-money-dollar-circle-line',
    address: 'TRQKgo59CFxvgHE4z4Zayu3Gmuzyo9rd1t',
    network: 'Tron (TRC-20)',
    addressType: 'TRC-20'
  },
  {
    id: 'solana',
    name: 'Solana',
    symbol: 'SOL',
    icon: 'ri-sun-line',
    address: 'HntriKBjw61zeB4Mftsj6aEhUEfBHQqUKKeM4hQ9zyi2',
    network: 'Solana Mainnet',
    addressType: 'Base58'
  },
  {
    id: 'bnb',
    name: 'BNB',
    symbol: 'BNB',
    icon: 'ri-coin-line',
    address: '0xfaF9e261Ac4fa5C48a187713c6F54119b17B8d8c',
    network: 'BSC Mainnet',
    addressType: 'BEP-20'
  },
  {
    id: 'litecoin',
    name: 'Litecoin',
    symbol: 'LTC',
    icon: 'ri-copper-diamond-line',
    address: 'MJutLTsPjGBmpYacm2GCK5rSJ8mw6YQoGJ',
    network: 'Litecoin Mainnet',
    addressType: 'P2SH'
  }
];

export default function BuySellPage() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState<CryptoOption>(PERMANENT_CRYPTO_ADDRESSES[0]);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [showMerchantModal, setShowMerchantModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [userEmail, setUserEmail] = useState('');

  // Fixed exchange rates
  const BUY_RATE = 0.003; // $0.003 per PepCoin
  const SELL_RATE = 0.001; // $0.001 per PepCoin

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setIsAuthenticated(!!user);
        if (user) {
          setUserEmail(user.email || '');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  // Fetch available merchants
  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        const { data, error } = await supabase
          .from('merchants')
          .select('id, name, email, wallet_balance, total_earnings')
          .eq('status', 'active')
          .gt('wallet_balance', 0)
          .order('wallet_balance', { ascending: false });

        if (error) throw error;

        // Add random ratings for display
        const merchantsWithRatings = (data || []).map(merchant => ({
          ...merchant,
          rating: 4.5 + Math.random() * 0.5 // Random rating between 4.5-5.0
        }));

        setMerchants(merchantsWithRatings);
      } catch (error) {
        console.error('Error fetching merchants:', error);
      }
    };

    fetchMerchants();
  }, []);

  // Fetch user wallet balance
  useEffect(() => {
    const fetchUserBalance = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: assets } = await supabase
          .from('user_assets')
          .select('balance')
          .eq('user_id', user.id)
          .eq('symbol', 'PEP')
          .single();

        if (assets) {
          setUserBalance(assets.balance || 0);
        }
      } catch (error) {
        console.error('Error fetching balance:', error);
      }
    };

    if (isAuthenticated) {
      fetchUserBalance();
    }
  }, [isAuthenticated]);

  const pepAmount = amount ? parseFloat(amount) / 0.003 : 0;

  const handleContinue = () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      navigate(`/auth/signup?redirect=/pepcoin/buy-sell&amount=${amount}`);
      return;
    }

    // Show merchant selection modal
    setShowMerchantModal(true);
  };

  const handleMerchantSelect = (merchant: Merchant) => {
    const pepRequired = pepAmount;
    
    if (merchant.wallet_balance < pepRequired) {
      alert(`This merchant doesn't have enough PepCoins. Available: ${merchant.wallet_balance.toLocaleString()} PEP`);
      return;
    }

    setSelectedMerchant(merchant);
    setShowMerchantModal(false);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async () => {
    if (!selectedMerchant) return;
    
    setIsProcessing(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('Please sign in to complete your purchase');
        navigate('/auth/signin');
        return;
      }

      // Call merchant transfer system
      const { data, error } = await supabase.functions.invoke('merchant-transfer-system', {
        body: {
          merchantId: selectedMerchant.id,
          buyerUserId: user.id,
          pepAmount: pepAmount,
          usdAmount: parseFloat(amount)
        }
      });

      if (error) {
        console.error('Payment error:', error);
        alert('Failed to process payment. Please contact support.');
        return;
      }

      if (data.success) {
        setShowPaymentModal(false);
        setShowSuccessModal(true);
        setAmount('');
        setSelectedMerchant(null);
        
        // Refresh balance
        const { data: assets } = await supabase
          .from('user_assets')
          .select('balance')
          .eq('user_id', user.id)
          .eq('symbol', 'PEP')
          .single();

        if (assets) {
          setUserBalance(assets.balance || 0);
        }
      } else {
        alert(data.message || 'Transaction failed');
      }

    } catch (error) {
      console.error('Payment error:', error);
      alert('An error occurred. Please contact support.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddFunds = () => {
    navigate('/payment/manual');
  };

  const handleBuyWithCrypto = () => {
    navigate('/payment/coinbase');
  };

  const generateQRCode = (address: string) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(address)}&bgcolor=FFFFFF&color=000000`;
  };

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <>
        <SEOHead 
          title="Buy PepCoin with Crypto - Major Cryptocurrency Exchange"
          description="Buy PepCoin using Bitcoin, Ethereum, USDT, Solana, BNB, and Litecoin. Secure cryptocurrency exchange with live pricing and instant transactions."
          keywords="buy pepcoin, cryptocurrency exchange, bitcoin, ethereum, USDT, solana, BNB, litecoin"
        />
        
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
          <Navbar />
          
          <div className="container mx-auto px-4 py-12 sm:py-20 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-white text-lg">Loading...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title="Buy PepCoin with Crypto - Major Cryptocurrency Exchange"
        description="Buy PepCoin using Bitcoin, Ethereum, USDT, Solana, BNB, and Litecoin. Secure cryptocurrency exchange with live pricing and instant transactions."
        keywords="buy pepcoin, cryptocurrency exchange, bitcoin, ethereum, USDT, solana, BNB, litecoin"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <Navbar />
        
        <div className="container mx-auto px-4 py-12 sm:py-20">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-12">
            <h1 className="text-2xl sm:text-4xl font-bold text-white mb-2">
              Buy PepCoin
            </h1>
            <p className="text-xs sm:text-base text-gray-300 max-w-2xl mx-auto">
              Purchase instantly from verified merchants
            </p>
          </div>

          {/* Not Authenticated - Show Signup Prompt */}
          {!isAuthenticated && (
            <div className="max-w-4xl mx-auto mb-8">
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-2 border-purple-500/50 rounded-2xl p-6 sm:p-8 backdrop-blur-lg">
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-add-line text-purple-300 text-3xl"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
                    Create Free Account to Buy PepCoin
                  </h2>
                  <p className="text-gray-300 text-sm sm:text-base mb-6 max-w-2xl mx-auto">
                    To purchase PepCoin, you need a free account. It only takes 30 seconds to sign up and you'll get instant access to buy, trade, and manage your PepCoin.
                  </p>

                  {/* Benefits */}
                  <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/5 rounded-xl p-4">
                      <i className="ri-gift-line text-green-400 text-2xl mb-2"></i>
                      <p className="text-white font-semibold text-sm">5,000 PEP Bonus</p>
                      <p className="text-gray-400 text-xs">Welcome reward</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4">
                      <i className="ri-flashlight-line text-purple-400 text-2xl mb-2"></i>
                      <p className="text-white font-semibold text-sm">Instant Access</p>
                      <p className="text-gray-400 text-xs">No verification wait</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4">
                      <i className="ri-shield-check-line text-blue-400 text-2xl mb-2"></i>
                      <p className="text-white font-semibold text-sm">100% Secure</p>
                      <p className="text-gray-400 text-xs">Bank-level security</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => navigate('/auth/signup?redirect=/pepcoin/buy-sell')}
                      className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all duration-300 hover:scale-105 shadow-lg cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-user-add-line mr-2"></i>
                      Create Free Account
                    </button>
                    <button
                      onClick={() => navigate('/auth/signin?redirect=/pepcoin/buy-sell')}
                      className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all duration-300 border border-white/30 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-login-box-line mr-2"></i>
                      Already Have Account? Sign In
                    </button>
                  </div>

                  <p className="text-gray-400 text-xs mt-4">
                    <i className="ri-time-line mr-1"></i>
                    Takes less than 30 seconds • No email verification required
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Balance Card - Only show if authenticated */}
          {isAuthenticated && (
            <div className="max-w-4xl mx-auto mb-6">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-4 sm:p-6 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-xs mb-1">Balance</p>
                    <p className="text-white text-xl sm:text-3xl font-bold">
                      {userBalance.toLocaleString()} PEP
                    </p>
                    {userEmail && (
                      <p className="text-purple-200 text-xs mt-1">{userEmail}</p>
                    )}
                  </div>
                  <div className="bg-white/20 rounded-full p-2.5 sm:p-4">
                    <i className="ri-coin-line text-white text-xl sm:text-3xl"></i>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl shadow-2xl p-4 sm:p-8 border border-gray-700">
              
              {/* Step 1: Select Cryptocurrency */}
              <div className="mb-6">
                <h2 className="text-base sm:text-xl font-bold text-white mb-3 flex items-center">
                  <span className="bg-purple-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs mr-2">1</span>
                  Select Crypto
                </h2>
                
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  {PERMANENT_CRYPTO_ADDRESSES.map((crypto) => (
                    <button
                      key={crypto.id}
                      onClick={() => setSelectedCrypto(crypto)}
                      className={`p-3 sm:p-5 rounded-xl border-2 transition-all cursor-pointer ${
                        selectedCrypto.id === crypto.id
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-gray-600 bg-gray-700/30 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center">
                          <i className={`${crypto.icon} text-xl sm:text-3xl text-purple-400`}></i>
                        </div>
                        <div className="text-left">
                          <p className="text-white font-semibold text-xs sm:text-base">{crypto.name}</p>
                          <p className="text-gray-400 text-xs">{crypto.symbol}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2: Enter Amount */}
              <div className="mb-6">
                <h2 className="text-base sm:text-xl font-bold text-white mb-3 flex items-center">
                  <span className="bg-purple-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs mr-2">2</span>
                  Enter Amount
                </h2>
                
                <div className="bg-gray-700/50 rounded-xl p-4 sm:p-6">
                  <label className="block text-gray-300 text-xs mb-2">
                    Amount (USD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base sm:text-xl">$</span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full bg-gray-800 text-white text-lg sm:text-2xl font-bold pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-4 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  
                  {amount && parseFloat(amount) > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-600">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-xs">You receive</span>
                        <span className="text-white text-base sm:text-xl font-bold">
                          {pepAmount.toLocaleString()} PEP
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs mt-1.5">Rate: 1 PEP = $0.003 USD</p>
                    </div>
                  )}
                </div>

                {/* Quick Amount Buttons */}
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {[50, 100, 500, 1000].map((value) => (
                    <button
                      key={value}
                      onClick={() => setAmount(value.toString())}
                      className="bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg transition-colors text-xs font-medium whitespace-nowrap cursor-pointer"
                    >
                      ${value}
                    </button>
                  ))}
                </div>

                {/* Add Funds Button - Only show if authenticated */}
                {isAuthenticated && (
                  <div className="mt-3">
                    <button
                      onClick={handleAddFunds}
                      className="w-full px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl text-white text-xs sm:text-sm font-medium hover:scale-105 transition-all duration-300 cursor-pointer shadow-lg shadow-cyan-500/20 whitespace-nowrap"
                    >
                      <i className="ri-add-line mr-1 text-xs"></i>
                      Add Funds
                    </button>
                    <p className="text-gray-500 text-xs mt-1.5 text-center">
                      Have funds? Continue above
                    </p>
                  </div>
                )}
              </div>

              {/* Continue Button */}
              <button
                onClick={handleContinue}
                disabled={!amount || parseFloat(amount) <= 0 || isProcessing}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-bold py-3 rounded-xl transition-all disabled:cursor-not-allowed cursor-pointer text-sm whitespace-nowrap"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Processing...
                  </span>
                ) : !isAuthenticated ? (
                  <>
                    <i className="ri-user-add-line mr-2"></i>
                    Create Account to Continue
                  </>
                ) : (
                  <>
                    <i className="ri-store-line mr-2"></i>
                    Choose Merchant
                  </>
                )}
              </button>

              {isAuthenticated && (
                <button
                  onClick={handleBuyWithCrypto}
                  className="w-full mt-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Buy with Crypto
                </button>
              )}

              {/* Info Box */}
              <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <i className="ri-information-line text-blue-400 text-base mt-0.5"></i>
                  <div>
                    <p className="text-blue-300 text-xs font-medium mb-0.5">
                      {!isAuthenticated ? 'Account Required' : 'Choose from Verified Merchants'}
                    </p>
                    <p className="text-blue-200/80 text-xs">
                      {!isAuthenticated 
                        ? 'Free account needed to buy PepCoin. Sign up takes 30 seconds.'
                        : `${merchants.length} merchants available with instant delivery`
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Merchant Selection Modal */}
        {showMerchantModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-700">
              <div className="p-5 sm:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold text-white">Select Merchant</h3>
                    <p className="text-gray-400 text-xs sm:text-sm mt-1">
                      Choose a verified merchant to buy {pepAmount.toLocaleString()} PEP
                    </p>
                  </div>
                  <button
                    onClick={() => setShowMerchantModal(false)}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl sm:text-2xl"></i>
                  </button>
                </div>

                {/* Merchants List */}
                {merchants.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-store-line text-gray-600 text-5xl mb-4"></i>
                    <p className="text-gray-400 text-sm">No merchants available at the moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {merchants.map((merchant) => (
                      <div
                        key={merchant.id}
                        onClick={() => handleMerchantSelect(merchant)}
                        className="bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-purple-500 rounded-xl p-4 transition-all cursor-pointer group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                <i className="ri-store-2-line text-white text-lg"></i>
                              </div>
                              <div>
                                <h4 className="text-white font-bold text-sm sm:text-base">
                                  {merchant.name}
                                </h4>
                                <div className="flex items-center space-x-1">
                                  {[...Array(5)].map((_, i) => (
                                    <i
                                      key={i}
                                      className={`ri-star-${i < Math.floor(merchant.rating) ? 'fill' : 'line'} text-yellow-400 text-xs`}
                                    ></i>
                                  ))}
                                  <span className="text-gray-400 text-xs ml-1">
                                    {merchant.rating.toFixed(1)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mt-3">
                              <div className="bg-gray-800/50 rounded-lg p-2">
                                <p className="text-gray-400 text-xs mb-1">Available</p>
                                <p className="text-white font-bold text-sm">
                                  {merchant.wallet_balance.toLocaleString()} PEP
                                </p>
                              </div>
                              <div className="bg-gray-800/50 rounded-lg p-2">
                                <p className="text-gray-400 text-xs mb-1">Total Sales</p>
                                <p className="text-white font-bold text-sm">
                                  ${parseFloat(merchant.total_earnings.toString()).toLocaleString()}
                                </p>
                              </div>
                            </div>

                            {merchant.wallet_balance < pepAmount && (
                              <div className="mt-2 bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                                <p className="text-red-400 text-xs">
                                  <i className="ri-error-warning-line mr-1"></i>
                                  Insufficient balance for this order
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="ml-4">
                            <button className="bg-purple-600 group-hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap">
                              Select
                              <i className="ri-arrow-right-line ml-1"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedMerchant && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-700">
              <div className="p-5 sm:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg sm:text-2xl font-bold text-white">Complete Payment</h3>
                  <button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setSelectedMerchant(null);
                    }}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl sm:text-2xl"></i>
                  </button>
                </div>

                {/* Merchant Info */}
                <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl p-4 border border-purple-500/30 mb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <i className="ri-store-2-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="text-white font-bold">{selectedMerchant.name}</p>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-${i < Math.floor(selectedMerchant.rating) ? 'fill' : 'line'} text-yellow-400 text-xs`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="space-y-4">
                  {/* Amount Summary */}
                  <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300 text-xs">Pay</span>
                      <span className="text-white text-lg sm:text-xl font-bold">
                        ${amount}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300 text-xs">Receive</span>
                      <span className="text-purple-300 text-base font-semibold">
                        {pepAmount.toLocaleString()} PEP
                      </span>
                    </div>
                  </div>

                  {/* Wallet Address */}
                  <div>
                    <label className="block text-gray-300 text-xs mb-2 font-medium">
                      Send {selectedCrypto.symbol} to:
                    </label>
                    <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                      <p className="text-white text-xs font-mono break-all mb-2.5">
                        {selectedCrypto.address}
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedCrypto.address);
                          alert('Address copied!');
                        }}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg transition-colors flex items-center justify-center text-xs font-medium whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-file-copy-line mr-1.5"></i>
                        Copy Address
                      </button>
                    </div>
                    <p className="text-gray-500 text-xs mt-1.5">Network: {selectedCrypto.network}</p>
                  </div>

                  {/* QR Code */}
                  <div className="text-center">
                    <p className="text-gray-300 text-xs mb-2.5 font-medium">Scan QR:</p>
                    <div className="bg-white p-3 sm:p-5 rounded-xl inline-block">
                      <img 
                        src={generateQRCode(selectedCrypto.address)} 
                        alt="Payment QR Code"
                        className="w-40 h-40 sm:w-56 sm:h-56"
                      />
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <i className="ri-alert-line text-yellow-400 text-base mt-0.5"></i>
                      <div>
                        <p className="text-yellow-300 text-xs font-medium mb-1">Important</p>
                        <ul className="text-yellow-200/80 text-xs space-y-0.5">
                          <li>• Send exact amount</li>
                          <li>• PepCoins delivered instantly</li>
                          <li>• Keep transaction ID</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => {
                        setShowPaymentModal(false);
                        setSelectedMerchant(null);
                      }}
                      className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-lg transition-colors text-xs font-medium whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePaymentComplete}
                      disabled={isProcessing}
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-600 text-white py-2.5 rounded-lg transition-all text-xs font-medium whitespace-nowrap cursor-pointer"
                    >
                      {isProcessing ? 'Processing...' : "I've Sent Payment"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl max-w-md w-full p-5 sm:p-8 shadow-2xl border border-gray-700">
              <div className="text-center">
                <div className="bg-green-500/20 w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-green-400 text-2xl sm:text-4xl"></i>
                </div>
                <h3 className="text-lg sm:text-2xl font-bold text-white mb-2">Purchase Complete!</h3>
                <p className="text-gray-300 text-xs sm:text-base mb-5">
                  {pepAmount.toLocaleString()} PEP has been added to your wallet
                </p>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/wallet');
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-2.5 sm:py-3 rounded-xl transition-all text-sm whitespace-nowrap cursor-pointer"
                >
                  Go to Wallet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
